import Component from '@ux/component';
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Display popover content when click on the control component
 *
 * @displayName PopOver (Web)
 * @class PopOver
 * @public
 */
class PopOver extends Component {
  constructor(props) {
    super(props);

    this.state = {
      open: props.open,
      alignment: props.popoverAlignment,
      controllerWidth: 0,
      caretStyleLeft: 0
    };

    this.mounted = false;
    this.toggle = this.toggle.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onHover = this.onHover.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
  }

  /**
   * Open the popover by setting open to true
   *
   * @public
   */
  open() {
    if (!this.mounted) return;
    this.setState(
      {
        open: true
      },
      () => this.props.onToggle(true)
    );
  }

  /**
   * Close the popover by setting open to false
   *
   * @public
   */
  close() {
    if (!this.mounted) return;
    this.setState(
      {
        open: false
      },
      () => this.props.onToggle(false)
    );
  }

  /**
   * Toggle the popover based on current state of open
   *
   * @param {SyntheticEvent} event React event.
   * @private
   */
  toggle(event) {
    event.stopPropagation();

    this.state.open ? this.close() : this.open();
  }

  /**
   * Hide the dropdown if clicks are outside the popover itself.
   */
  componentDidMount() {
    this.mounted = true;
    this.clickHandler = this.outside('click', this.close, this.contentWrapper);
    this.focusInHandler = this.outside(
      'focus',
      this.close,
      this.contentWrapper
    );
    const alignment = this.getAlignment();
    this.setState(
      {
        alignment
      },
      () => this.calculateCaretPosition()
    );
  }

  /**
   * Remove the attached click event listener.
   */
  componentWillUnmount() {
    this.mounted = false;
    this.off('click', this.clickHandler);
    this.off('focus', this.focusInHandler);
  }

  /**
   * Check if the open prop has changed.
   * If so, update state of open.
   *
   * @param {Object} prevProps - previous instance of props
   * @param {Object} prevState - previous instance of state
   */
  componentDidUpdate(prevProps, prevState) {
    const { open } = this.state;
    const { popoverAlignment } = this.props;

    if (popoverAlignment && popoverAlignment !== prevProps.popoverAlignment) {
      this.setState({
        alignment: popoverAlignment
      });
    } else if (open && open !== prevState.open) {
      const alignment = this.getAlignment();
      if (alignment !== prevState.alignment) {
        this.setState(
          {
            alignment
          },
          () => this.calculateCaretPosition()
        );
      }
    }

    if (
      prevProps.open !== this.props.open &&
      this.state.open !== this.props.open
    ) {
      this.setState({
        open: this.props.open
      });
    }

    if (prevState.controllerWidth !== this.state.controllerWidth) {
      this.calculateCaretPosition();
    }
  }

  /**
   * Listen for the ESC key event.
   * @param {object} e the event object sent from the browser
   * @api private
   */
  handleEscape(e) {
    if (e.key && (e.key === 'Escape' || e.key === 'Esc')) {
      e.preventDefault();
      this.close();
    }
  }

  onHover(hovered) {
    if (!this.props.openOnHover) {
      return;
    }

    this.stopAutoHideTimer();

    if (hovered) {
      this.open();
    } else {
      this.autoHideTimerHandle = window.setTimeout(
        this.close,
        this.props.autoHideTimeout
      );
    }
  }

  /**
   * Stops (clears) auto hide timer
   *
   * @private
   */
  stopAutoHideTimer() {
    if (this.autoHideTimerHandle) {
      window.clearTimeout(this.autoHideTimerHandle);
      this.autoHideTimerHandle = null;
    }
  }

  /**
   * Fetches all element bounds to necessary calculations of display
   *
   * @private
   * @returns {object} element bounds
   */
  getElementBounds() {
    // Viewport Bounds
    const viewportBounds = {
      x: window.pageXOffset,
      y: window.pageYOffset,
      width: window.innerWidth,
      height: window.innerHeight
    };

    // Wrapper Div for Popover
    const wrapperDivBounds =
      this.contentWrapper && this.contentWrapper.getBoundingClientRect();

    // Popover Element
    const menuBounds =
      this.popoverElement && this.popoverElement.getBoundingClientRect();

    return {
      viewportBounds,
      wrapperDivBounds,
      menuBounds
    };
  }

  /**
   * Compute horizontal spacing for menu and determine whether to show aligned left or right to the popover.
   *
   * @returns {String|Boolean} The direction of alignment. False if neither left nor right.
   * @private
   */
  getAlignment() {
    let alignment = '';
    const {
      wrapperDivBounds,
      menuBounds,
      viewportBounds
    } = this.getElementBounds();
    if (wrapperDivBounds && menuBounds) {
      const right =
        wrapperDivBounds.left +
        viewportBounds.x -
        window.document.documentElement.clientLeft +
        menuBounds.width;

      // The element needs to be right aligned if it's too close or outside the bounds of the viewport
      // and if it is small enough to fit inside a viewport, if it's too big we'll default to left
      if (
        viewportBounds.width - right < 10 &&
        menuBounds.width < viewportBounds.width
      ) {
        alignment = 'right';
      } else {
        // otherwise align left
        alignment = 'left';
      }
    }

    return alignment;
  }

  /**
   * Calculate caret position based on controller element and alignment
   *
   * @private
   */
  calculateCaretPosition() {
    const controllerWidth = this.contentWrapper
      ? this.contentWrapper.getBoundingClientRect().width
      : void 0;
    const result = {
      controllerWidth
    };

    // Remark: (right|left)Style = (controllerWidth/2) - (caretWidth/2)
    result.caretPosition = controllerWidth / 2 - 8;

    this.setState(result);
  }

  /**
   * Render a properly positioned caret
   *
   * @returns {ReactElement} PopOver element.
   * @private
   */
  renderCaret() {
    const { caretPosition, alignment } = this.state;
    const styles = {};

    if (caretPosition) {
      styles[alignment] = caretPosition;
    }

    return <div className='content-caret' style={ styles }></div>;
  }

  /**
   * Render the control component within a button and the popover content children.
   *
   * @returns {ReactElement} PopOver element.
   * @private
   */
  render() {
    const { children, className, control, eid } = this.props;

    const { open, alignment } = this.state;

    // check that we are in viewport bounds && bidi?
    const alignClass =
      alignment === 'left' ? 'popover-align-left' : 'popover-align-right';

    const wrapperClassNames = this.classNames('popover', alignClass, className, {
      open
    });

    return (
      <div
        className={ wrapperClassNames }
        data-eid={ eid }
        ref={ r => {
          this.contentWrapper = r;
        } }
        onMouseEnter={ () => this.onHover(true) }
        onMouseLeave={ () => this.onHover(false) }
        onKeyUp={ this.handleEscape }
        role='presentation'>
        <button
          type='button'
          onClick={ this.toggle }
          tabIndex='0'
          className='popover-button'>
          { control }
        </button>
        {this.renderCaret()}
        <div
          className='content shadow-4'
          ref={ r => {
            this.popoverElement = r;
          } }>
          { children }
        </div>
      </div>
    );
  }
}

PopOver.propTypes = {
  /** The number of milliseconds before popover auto-hides (use in tandem w/ openOnHover) */
  autoHideTimeout: PropTypes.number,

  /** Customized class name */
  className: PropTypes.string,

  /** Control component for button render  */
  control: PropTypes.element,

  /** Toggle handler for when popover is opened or closed */
  onToggle: PropTypes.func,

  /** eid to be used for the traffic tracking */
  eid: PropTypes.string,

  /** Allows consumer to open or close the popover */
  open: PropTypes.bool,

  /** Flag dictating if popover should open on hover */
  openOnHover: PropTypes.bool,

  /** Aligns popover on left or right */
  popoverAlignment: PropTypes.oneOf(['left', 'right']),

  /** React children */
  children: PropTypes.element.isRequired
};

PopOver.defaultProps = {
  autoHideTimeout: 75,
  onToggle: () => {},
  open: false,
  openOnHover: false,
  popoverAlignment: 'left'
};

export default PopOver;
